import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  Search,
  Command,
  LayoutDashboard,
  Users,
  Settings,
  CreditCard,
  Link2,
  Shield,
  ArrowRight,
  X,
  HelpCircle,
  Keyboard
} from 'lucide-react';

const CommandPalette = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { api, isAdmin } = useAuth();
  const [query, setQuery] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const currentWorkspaceId = localStorage.getItem('currentWorkspaceId');

  // Fetch data when opened
  useEffect(() => {
    if (isOpen) {
      fetchData();
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      const [wsRes, saRes] = await Promise.all([
        api.get('/api/workspaces'),
        currentWorkspaceId ? api.get(`/api/workspaces/${currentWorkspaceId}/sub-accounts`) : Promise.resolve({ data: [] })
      ]);
      setWorkspaces(wsRes.data);
      setSubAccounts(saRes.data || []);
    } catch (error) {
      console.error('Failed to fetch command palette data:', error);
    }
  };

  // Build command list
  const commands = [
    // Navigation
    { id: 'nav-dashboard', type: 'navigation', label: 'Go to Dashboard', icon: LayoutDashboard, action: () => navigate(`/workspace/${currentWorkspaceId}`) },
    { id: 'nav-subaccounts', type: 'navigation', label: 'Go to Sub-Accounts', icon: Users, action: () => navigate(`/workspace/${currentWorkspaceId}/sub-accounts`) },
    { id: 'nav-integrations', type: 'navigation', label: 'Go to Integrations', icon: Link2, action: () => navigate(`/workspace/${currentWorkspaceId}/integrations`) },
    { id: 'nav-billing', type: 'navigation', label: 'Go to Billing', icon: CreditCard, action: () => navigate(`/workspace/${currentWorkspaceId}/billing`) },
    { id: 'nav-settings', type: 'navigation', label: 'Go to Settings', icon: Settings, action: () => navigate('/settings') },
    ...(isAdmin ? [{ id: 'nav-admin', type: 'navigation', label: 'Go to Admin Panel', icon: Shield, action: () => navigate('/admin') }] : []),
    
    // Workspaces
    ...workspaces.map(ws => ({
      id: `ws-${ws.workspace_id}`,
      type: 'workspace',
      label: `Switch to ${ws.name}`,
      sublabel: ws.is_lifetime ? 'Lifetime' : (!ws.plan || ws.plan === 'launchpad') ? 'Pay as you go' : `${ws.plan} plan`,
      icon: LayoutDashboard,
      color: ws.color,
      action: () => {
        localStorage.setItem('currentWorkspaceId', ws.workspace_id);
        navigate(`/workspace/${ws.workspace_id}`);
      }
    })),
    
    // Sub-accounts
    ...subAccounts.map(sa => ({
      id: `sa-${sa.sub_account_id}`,
      type: 'subaccount',
      label: sa.name,
      sublabel: `${sa.status} • ${sa.calls || 0} calls`,
      icon: Users,
      action: () => navigate(`/workspace/${currentWorkspaceId}/sub-accounts`)
    }))
  ];

  // Filter commands based on query
  const filteredCommands = commands.filter(cmd => 
    cmd.label.toLowerCase().includes(query.toLowerCase()) ||
    (cmd.sublabel && cmd.sublabel.toLowerCase().includes(query.toLowerCase()))
  );

  // Group commands by type
  const groupedCommands = {
    navigation: filteredCommands.filter(c => c.type === 'navigation'),
    workspace: filteredCommands.filter(c => c.type === 'workspace'),
    subaccount: filteredCommands.filter(c => c.type === 'subaccount')
  };

  const allFiltered = [...groupedCommands.navigation, ...groupedCommands.workspace, ...groupedCommands.subaccount];

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allFiltered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && allFiltered[selectedIndex]) {
      e.preventDefault();
      allFiltered[selectedIndex].action();
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [allFiltered, selectedIndex, onClose]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const CommandGroup = ({ title, commands, startIndex }) => {
    if (commands.length === 0) return null;
    return (
      <div className="mb-2">
        <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</div>
        {commands.map((cmd, idx) => {
          const globalIndex = startIndex + idx;
          const Icon = cmd.icon;
          return (
            <button
              key={cmd.id}
              onClick={() => { cmd.action(); onClose(); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                selectedIndex === globalIndex 
                  ? 'bg-teal-400/10 text-teal-400' 
                  : 'text-gray-300 hover:bg-white/5'
              }`}
            >
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: cmd.color ? `${cmd.color}20` : 'rgba(255,255,255,0.05)' }}
              >
                <Icon size={16} style={{ color: cmd.color || (selectedIndex === globalIndex ? '#14b8a6' : '#9ca3af') }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{cmd.label}</p>
                {cmd.sublabel && <p className="text-xs text-gray-500 truncate">{cmd.sublabel}</p>}
              </div>
              <ArrowRight size={14} className="text-gray-500" />
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.15 }}
          className="w-full max-w-xl mx-auto mt-[15vh]"
          onClick={e => e.stopPropagation()}
        >
          <div className="bg-space-800 border border-white/10 rounded-xl shadow-2xl overflow-hidden">
            {/* Search Input */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Search size={20} className="text-gray-500" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={e => { setQuery(e.target.value); setSelectedIndex(0); }}
                  placeholder="Search workspaces, sub-accounts, or commands..."
                  className="flex-1 bg-transparent text-white text-lg placeholder-gray-500 focus:outline-none"
                  data-testid="command-palette-input"
                />
                <button
                  onClick={onClose}
                  className="p-1 rounded text-gray-500 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Commands List */}
            <div className="max-h-[400px] overflow-y-auto py-2">
              {allFiltered.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  <Search size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No results found for "{query}"</p>
                </div>
              ) : (
                <>
                  <CommandGroup 
                    title="Navigation" 
                    commands={groupedCommands.navigation} 
                    startIndex={0} 
                  />
                  <CommandGroup 
                    title="Workspaces" 
                    commands={groupedCommands.workspace} 
                    startIndex={groupedCommands.navigation.length} 
                  />
                  <CommandGroup 
                    title="Sub-Accounts" 
                    commands={groupedCommands.subaccount} 
                    startIndex={groupedCommands.navigation.length + groupedCommands.workspace.length} 
                  />
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-gray-400">↑↓</kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-gray-400">↵</kbd>
                  Select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-gray-400">Esc</kbd>
                  Close
                </span>
              </div>
              <span className="flex items-center gap-1">
                <Command size={12} />
                <span>+ K to open</span>
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Hook to use command palette
export const useCommandPalette = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return { isOpen, setIsOpen, open: () => setIsOpen(true), close: () => setIsOpen(false) };
};

export default CommandPalette;
